import React, { useState, useEffect } from "react";
import { Card, Button, Form, Icon, Input, Segment, Statistic, Label, Divider, Loader, Header, Grid } from "semantic-ui-react";
import { CashRegister } from "../../../api";
import { useAuth } from "../../../hooks";
import { CustomTable } from "../../Shared";
import Swal from "sweetalert2";
import "./CashRegisterPanel.scss";

const cashRegisterController = new CashRegister();

export function CashRegisterPanel() {
  const { accessToken } = useAuth();
  
  const [activeRegister, setActiveRegister] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState(null);

  // Open Form
  const [openingBalance, setOpeningBalance] = useState("0");
  const [openingLoading, setOpeningLoading] = useState(false);

  // Close Form
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingBalance, setClosingBalance] = useState("0");
  const [closingLoading, setClosingLoading] = useState(false);

  const onReload = () => setReload((prev) => !prev);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      
      // Active Register
      try {
        const activeRes = await cashRegisterController.getActiveCashRegister(accessToken);
        if (activeRes && activeRes.success && activeRes.register) {
          setActiveRegister(activeRes.register);
        } else {
          setActiveRegister(null);
        }
      } catch (error) {
        console.error("Error al obtener estado de caja activa:", error);
        setActiveRegister(null);
      }

      // History
      try {
        const historyRes = await cashRegisterController.getCashRegisters(accessToken, { 
          page: currentPage, 
          limit: itemsPerPage 
        });
        setHistory(historyRes?.registers || []);
        setPagination(historyRes?.pagination || {
          total: historyRes?.registers?.length || 0,
          pages: 1,
          page: currentPage,
          limit: itemsPerPage
        });
      } catch (error) {
        console.error("Error al obtener historial de cajas:", error);
        setHistory([]);
        setPagination(null);
      }

      setLoading(false);
    })();
  }, [reload, accessToken, currentPage, itemsPerPage]);

  const getDiff = (row) => {
    const exp = (parseFloat(row.openingBalance || 0) + parseFloat(row.totalIncomes || 0)) - parseFloat(row.totalExpenses || 0);
    return parseFloat(row.closingBalance || 0) - exp;
  };

  const getStatusColor = (status) => {
    return status === "OPEN" ? "green" : "grey";
  };

  const getStatusText = (status) => {
    return status === "OPEN" ? "ABIERTA" : "CERRADA";
  };

  const tableConfig = {
    headers: [
      "Apertura",
      "Cierre",
      "Inicial",
      "Ingresos",
      "Egresos",
      "Cierre Físico",
      "Diferencia",
      "Estado",
    ],
    fields: [
      {
        field: "openedAt",
        component: "custom",
        render: (row) => new Date(row.openedAt).toLocaleString(),
      },
      {
        field: "closedAt",
        component: "custom",
        render: (row) => row.closedAt ? new Date(row.closedAt).toLocaleString() : "Caja activa",
      },
      {
        field: "openingBalance",
        component: "custom",
        align: "right",
        render: (row) => `S/ ${Number(row.openingBalance).toFixed(2)}`,
      },
      {
        field: "totalIncomes",
        component: "custom",
        align: "right",
        render: (row) => (
          <span className="text-success">
            S/ {Number(row.totalIncomes || 0).toFixed(2)}
          </span>
        ),
      },
      {
        field: "totalExpenses",
        component: "custom",
        align: "right",
        render: (row) => (
          <span className="text-danger">
            S/ {Number(row.totalExpenses || 0).toFixed(2)}
          </span>
        ),
      },
      {
        field: "closingBalance",
        component: "custom",
        align: "right",
        render: (row) => row.closingBalance ? `S/ ${Number(row.closingBalance).toFixed(2)}` : "-",
      },
      {
        field: "difference",
        component: "custom",
        align: "right",
        render: (row) => {
          if (!row.closedAt) return "-";
          const diff = getDiff(row);
          const diffClass = diff < 0 ? "text-danger" : diff > 0 ? "text-success" : "";
          return (
            <span className={diffClass}>
              S/ {diff.toFixed(2)}
            </span>
          );
        },
      },
      {
        field: "status",
        component: "Label",
        align: "center",
        getColor: getStatusColor,
        getText: getStatusText,
      },
    ],
    columnWidths: ["15%", "15%", "11%", "11%", "11%", "11%", "11%", "15%"],
  };


  const handleOpenRegister = async () => {
    try {
      const balanceVal = parseFloat(openingBalance);
      if (isNaN(balanceVal) || balanceVal < 0) {
        Swal.fire({
          icon: "warning",
          title: "Valor Inválido",
          text: "El monto de apertura debe ser un número positivo.",
          background: "#1f2937",
          color: "#fff",
        });
        return;
      }
      setOpeningLoading(true);
      await cashRegisterController.openCashRegister(accessToken, balanceVal);
      Swal.fire({
        icon: "success",
        title: "Caja Abierta",
        text: `Caja abierta con un saldo inicial de S/ ${balanceVal.toFixed(2)}`,
        background: "#1f2937",
        color: "#fff",
        confirmButtonColor: "#3b82f6",
      });
      onReload();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "No se pudo abrir la caja.",
        background: "#1f2937",
        color: "#fff",
      });
    } finally {
      setOpeningLoading(false);
    }
  };

  const handleCloseRegister = async () => {
    try {
      const balanceVal = parseFloat(closingBalance);
      if (isNaN(balanceVal) || balanceVal < 0) {
        Swal.fire({
          icon: "warning",
          title: "Valor Inválido",
          text: "El monto de cierre debe ser un número positivo.",
          background: "#1f2937",
          color: "#fff",
        });
        return;
      }

      const expected = (parseFloat(activeRegister.openingBalance || 0) + 
                        parseFloat(activeRegister.totalIncomes || 0)) - 
                       parseFloat(activeRegister.totalExpenses || 0);

      const diff = balanceVal - expected;
      
      let warningText = `Se cerrará la caja con un balance físico de S/ ${balanceVal.toFixed(2)}.`;
      if (diff !== 0) {
        warningText += `\n\nATENCIÓN: Hay una diferencia de S/ ${diff.toFixed(2)} respecto al balance esperado (S/ ${expected.toFixed(2)}).`;
      }

      const confirmRes = await Swal.fire({
        title: "¿Cerrar Caja?",
        text: warningText,
        icon: diff !== 0 ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: "Sí, cerrar caja",
        cancelButtonText: "Cancelar",
        confirmButtonColor: diff !== 0 ? "#d97706" : "#3b82f6",
        background: "#1f2937",
        color: "#fff",
      });

      if (!confirmRes.isConfirmed) return;

      setClosingLoading(true);
      await cashRegisterController.closeCashRegister(accessToken, balanceVal);
      setShowCloseModal(false);
      Swal.fire({
        icon: "success",
        title: "Caja Cerrada",
        text: "La caja ha sido cerrada y los balances registrados.",
        background: "#1f2937",
        color: "#fff",
        confirmButtonColor: "#3b82f6",
      });
      onReload();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "No se pudo cerrar la caja.",
        background: "#1f2937",
        color: "#fff",
      });
    } finally {
      setClosingLoading(false);
    }
  };

  const getExpectedBalance = () => {
    if (!activeRegister) return 0;
    return (parseFloat(activeRegister.openingBalance || 0) + 
            parseFloat(activeRegister.totalIncomes || 0)) - 
           parseFloat(activeRegister.totalExpenses || 0);
  };

  if (loading) {
    return <Loader active inline="centered" content="Cargando panel de caja..." />;
  }

  return (
    <div className="cash-register-panel">
      <div className="cash-register-panel__header">
        <Header as="h2">
          <Icon name="archive" color="teal" />
          <Header.Content>
            Control de Caja y Flujo de Caja
            <Header.Subheader>Administración de apertura, cierre de caja y arqueo diario</Header.Subheader>
          </Header.Content>
        </Header>
      </div>

      <Grid stackable columns={activeRegister ? 2 : 1}>
        <Grid.Row>
          {/* Main Control Card */}
          <Grid.Column width={activeRegister ? 10 : 16}>
            {!activeRegister ? (
              <Segment placeholder className="cash-register-panel__status-segment closed">
                <Header icon>
                  <Icon name="lock" color="red" />
                  La caja se encuentra CERRADA
                  <Header.Subheader>Para poder registrar ingresos o egresos, debes iniciar la jornada abriendo la caja.</Header.Subheader>
                </Header>
                <div className="opening-form">
                  <Form onSubmit={handleOpenRegister}>
                    <Form.Field>
                      <label>Saldo Inicial de Apertura (S/)</label>
                      <Input
                        type="number"
                        step="0.01"
                        icon="money"
                        iconPosition="left"
                        placeholder="Ej. 100.00"
                        value={openingBalance}
                        onChange={(e) => setOpeningBalance(e.target.value)}
                      />
                    </Form.Field>
                    <Button primary fluid type="submit" loading={openingLoading}>
                      <Icon name="key" /> Abrir Caja
                    </Button>
                  </Form>
                </div>
              </Segment>
            ) : (
              <Segment className="cash-register-panel__status-segment open">
                <div className="header-status">
                  <Label color="green" size="large" ribbon>
                    <Icon name="unlock" /> CAJA ABIERTA
                  </Label>
                  <span className="opened-at">
                    Apertura: {new Date(activeRegister.openedAt).toLocaleString()}
                  </span>
                </div>

                <div className="stats-container">
                  <Statistic.Group size="mini" widths="four">
                    <Statistic>
                      <Statistic.Value>S/ {Number(activeRegister.openingBalance).toFixed(2)}</Statistic.Value>
                      <Statistic.Label>Saldo Inicial</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value className="text-success">S/ {Number(activeRegister.totalIncomes || 0).toFixed(2)}</Statistic.Value>
                      <Statistic.Label>Ingresos</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value className="text-danger">S/ {Number(activeRegister.totalExpenses || 0).toFixed(2)}</Statistic.Value>
                      <Statistic.Label>Egresos</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value className="text-primary-color">S/ {getExpectedBalance().toFixed(2)}</Statistic.Value>
                      <Statistic.Label>Saldo Esperado</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>
                </div>

                <Divider />

                <div className="action-buttons" style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button color="orange" onClick={() => { setClosingBalance(getExpectedBalance().toFixed(2)); setShowCloseModal(true); }}>
                    <Icon name="lock" /> Cerrar y Cuadrar Caja
                  </Button>
                </div>
              </Segment>
            )}
          </Grid.Column>

          {/* Quick Info Column when open */}
          {activeRegister && (
            <Grid.Column width={6}>
              <Card fluid className="cash-register-panel__info-card">
                <Card.Content>
                  <Card.Header><Icon name="info circle" color="blue" /> Información de Auditoría</Card.Header>
                  <Divider />
                  <div className="audit-details">
                    <p><strong>Cajero responsable:</strong> {activeRegister.openedByUser?.name || "Administrador"}</p>
                    <p><strong>ID Caja Activa:</strong> #{activeRegister.id}</p>
                    <p><strong>Moneda de trabajo:</strong> Soles (S/)</p>
                    <p className="audit-note">
                      * El saldo esperado se actualiza en tiempo real al confirmar reportes de pago y registrar egresos en caja.
                    </p>
                  </div>
                </Card.Content>
              </Card>
            </Grid.Column>
          )}
        </Grid.Row>
      </Grid>

      {/* History Log */}
      <div className="cash-register-panel__history">
        <Header as="h3">
          <Icon name="history" />
          Historial de Cajas Cerradas
        </Header>
        <div className="table-wrapper">
          <CustomTable
            color="teal"
            tableConfig={tableConfig}
            data={history}
            pagination={pagination}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onReload={onReload}
          />
        </div>
      </div>

      {/* Close Cash Register Modal */}
      {showCloseModal && (
        <Segment className="close-caja-modal" style={{ position: "fixed", top: "25%", left: "35%", width: "30%", zIndex: 1000, background: "var(--card-bg)", border: "1px solid var(--border-grey-light)", borderRadius: "8px", padding: "2rem", boxShadow: "var(--card-shadow)" }}>
          <Header as="h3" style={{ color: "var(--text-color)" }}><Icon name="lock" color="orange" /> Cerrar Caja Activa</Header>
          <Divider style={{ borderColor: "var(--border-grey-light)" }} />
          <Form onSubmit={handleCloseRegister}>
            <Form.Field>
              <label style={{ color: "var(--text-color)" }}>Total de Efectivo / Medios Físicos en Caja (S/)</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Ingrese total físico..."
                value={closingBalance}
                onChange={(e) => setClosingBalance(e.target.value)}
                autoFocus
              />
            </Form.Field>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
              <Button type="button" onClick={() => setShowCloseModal(false)}>Cancelar</Button>
              <Button color="orange" type="submit" loading={closingLoading}>Cerrar Caja</Button>
            </div>
          </Form>
        </Segment>
      )}
    </div>
  );
}
